import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, useAuth, useLang, formatApiError, setAuthToken } from "../lib/providers";
import { Receipt, Translate, X, Check } from "@phosphor-icons/react";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const { refresh } = useAuth();
  const { t, lang, toggleLang } = useLang();
  const navigate = useNavigate();

  const [valid, setValid] = useState(null); // null=loading, true=ok, false=invalid
  const [tokenInfo, setTokenInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/auth/reset-password/${token}`);
        setTokenInfo(data);
        setValid(true);
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || err.message);
        setValid(false);
      }
    })();
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError(t("passwordMismatch"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      if (data?.access_token) setAuthToken(data.access_token);
      toast.success(t("resetSuccess"));
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[var(--brand)] flex items-center justify-center">
              <Receipt weight="fill" size={14} color="white" />
            </div>
            <span className="font-heading font-semibold">FalkeDeck</span>
          </div>
          <button onClick={toggleLang} data-testid="reset-lang-switch"
            className="flex items-center gap-1.5 text-xs text-[var(--muted)] hover:text-[var(--ink)] transition-colors">
            <Translate size={14} />
            <span className="label-overline">{lang.toUpperCase()}</span>
          </button>
        </div>

        <div className="label-overline mb-2">{t("resetTitle")}</div>

        {valid === null ? (
          <div className="text-[var(--muted)] text-sm">…</div>
        ) : valid === false ? (
          <div data-testid="reset-invalid">
            <h2 className="font-heading text-2xl font-semibold tracking-tight flex items-center gap-2 text-[var(--ink)]">
              <X size={24} className="text-[#FF3B30]" />
              {lang === "de" ? "Link ungültig" : "Link invalid"}
            </h2>
            <p className="text-sm text-[var(--muted)] mt-3">
              {lang === "de"
                ? "Dieser Reset-Link ist ungültig oder bereits abgelaufen. Bitte fordere einen neuen Link an."
                : "This reset link is invalid or has expired. Please request a new one."}
            </p>
            <Link
              to="/forgot-password"
              data-testid="reset-request-new"
              className="inline-block mt-6 text-[var(--brand)] hover:underline text-sm"
            >
              {lang === "de" ? "Neuen Link anfordern" : "Request a new link"}
            </Link>
          </div>
        ) : (
          <>
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-[var(--ink)]">
              {t("resetTitle")}
            </h2>
            <p className="text-[var(--muted)] mt-2 text-sm">
              {t("resetSubtitle")}
              {tokenInfo?.email && (
                <>
                  {" "}
                  <span className="text-[var(--ink)] font-medium">{tokenInfo.email}</span>
                </>
              )}
            </p>

            <form onSubmit={onSubmit} className="mt-8 space-y-5">
              <div>
                <label className="label-overline block mb-1.5">{t("newPassword")}</label>
                <input
                  data-testid="reset-password"
                  type="password"
                  required
                  minLength={8}
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 px-3 bg-[var(--surface)] border border-[var(--border)] rounded-sm text-sm text-[var(--ink)] focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand-soft)] focus:outline-none"
                />
              </div>
              <div>
                <label className="label-overline block mb-1.5">{t("confirmPassword")}</label>
                <input
                  data-testid="reset-password-confirm"
                  type="password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full h-11 px-3 bg-[var(--surface)] border border-[var(--border)] rounded-sm text-sm text-[var(--ink)] focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand-soft)] focus:outline-none"
                />
              </div>
              {error && (
                <div data-testid="reset-error" className="text-sm text-[#FF3B30] border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-3 py-2 rounded-sm">
                  {error}
                </div>
              )}
              <button
                data-testid="reset-submit"
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white font-medium text-sm rounded-sm transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                <Check size={16} /> {submitting ? "…" : t("resetSubmit")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
